import _ from 'lodash';
import type { MutableRefObject } from 'react';

export function scrollToBottom<T extends HTMLElement>(scrollElementRef: MutableRefObject<T>) {
  scrollElementRef.current.scrollTo({
    behavior: 'smooth',
    top: scrollElementRef.current.scrollHeight,
  });
}

export function scrollTo<T extends HTMLElement | undefined>(
  scrollElementRef: MutableRefObject<T>,
  top: number,
  containerPath = '',
) {
  const container = _.get(scrollElementRef.current, containerPath, scrollElementRef.current);
  (container as HTMLElement).scrollTo({
    behavior: 'smooth',
    top,
  });
}

function findScrollableParent(element: HTMLElement): HTMLElement | null {
  let current = element.parentElement;
  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * Scrolls an element to the top of the nearest ancestor a user can actually scroll.
 *
 * `Element.scrollIntoView` scrolls every scrollable ancestor, including `overflow: hidden` ones that offer no
 * scrollbar to scroll back with, which leaves those ancestors permanently offset and clips whatever they contain.
 */
export function scrollToTopOfScrollParent(element: HTMLElement) {
  const scrollContainer = findScrollableParent(element);
  if (!scrollContainer) {
    return;
  }

  // Convert the element's viewport position into the scroll container's coordinate space:
  // (element top relative to container) + current scrollTop = absolute scroll position we should scroll to.
  const containerRect = scrollContainer.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  scrollContainer.scrollTo({
    top: scrollContainer.scrollTop + (elementRect.top - containerRect.top),
    behavior: 'smooth',
  });
}

export function getFavIconElement(): HTMLAnchorElement {
  return document.getElementById('favicon') as HTMLAnchorElement;
}

/**
 * Focuses and selects the text content of an HTML input element when triggered by a mouse event.
 *
 * @param event - The focus event that triggers the function, typically from a React component.
 */
export function focusAndSelectElement(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
  event.currentTarget.focus();
  event.currentTarget.select();
}

const CARET_MIRROR_PROPERTIES = [
  'boxSizing',
  'width',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'letterSpacing',
  'lineHeight',
  'textTransform',
  'textRendering',
] as const;

/**
 * Tiny helper that determines where to place the "suggestions" if you would like to display a list of options when
 * a user is typing into a multi-line textarea
 */
export function getTextAreaCaretTopOffset(input: HTMLTextAreaElement, anchor: HTMLElement): number {
  const computed = window.getComputedStyle(input);
  const fontSize = Number.parseFloat(computed.fontSize || '0');
  const rawLineHeight = Number.parseFloat(computed.lineHeight || '0');
  const resolvedLineHeight = Number.isFinite(rawLineHeight) && rawLineHeight > 0 ? rawLineHeight : fontSize * 1.5;
  const caretIndex = input.selectionStart ?? input.value.length;

  const mirror = document.createElement('div');
  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.wordWrap = 'break-word';
  mirror.style.top = '0';
  mirror.style.left = '-9999px';
  mirror.style.overflow = 'hidden';
  CARET_MIRROR_PROPERTIES.forEach((property) => {
    mirror.style[property] = computed[property] as string;
  });
  mirror.textContent = input.value.substring(0, caretIndex);
  const marker = document.createElement('span');
  marker.textContent = input.value.substring(caretIndex) || '.';
  mirror.appendChild(marker);
  let caretTop = 0;
  document.body.appendChild(mirror);
  try {
    caretTop = marker.offsetTop;
  } finally {
    document.body.removeChild(mirror);
  }

  const anchorRect = anchor.getBoundingClientRect();
  const inputRect = input.getBoundingClientRect();
  return inputRect.top - anchorRect.top + caretTop - input.scrollTop + resolvedLineHeight + 6;
}
